/**
 * The session reducer — the deterministic heart of the application.
 *
 * Everything the UI draws is a function of `SessionState`, and `SessionState` only ever
 * changes here. The reducer is pure in the strong sense (ADR 11): no `fetch`, no
 * `Date.now()`, no `crypto.randomUUID()`, no `Math.random()`. Timestamps ride in on the
 * action (`DetectedChord.timestamp`); ids are derived deterministically from the path
 * position and that timestamp, or from an injected factory. A session is therefore
 * replayable from its action log, which is how a recogniser regression gets diagnosed
 * from a bug report rather than from a piano.
 *
 * `CHORD_DETECTED` is the transition the product turns on; its required semantics are
 * spelled out on the action type in `types.ts` and implemented below in the same order.
 *
 * ## The rule that is easy to lose in a refactor
 *
 * Playing a chord nobody suggested is **not an error** (ADR 12). There is no rejection
 * path in this file, no "unrecognised" flag, and no snapping to the nearest branch. If
 * you find yourself adding one, the product has changed, not the code.
 */

import { chordDegreeSemitones, respell, toCanonical } from '../chord/canonical.js';
import { mod12, spellPitchClass, type KeyContext } from '../chord/enharmonic.js';
import { sortExtensions } from '../chord/parse.js';
import { pcSetEquals, pcSetIsStrictSuperset } from '../chord/pcset.js';
import {
  EXTENSION_DEGREES,
  type CanonicalChord,
  type ChordSpec,
  type Extension,
  type PitchClass,
} from '../chord/types.js';
import {
  DEFAULT_RECOGNIZER_PARAMS,
  type ChordHypothesis,
  type DetectedChord,
} from '../recognition/types.js';
import { estimateKey } from './keyEstimate.js';
import { findSuggestionByPitchClasses } from './matchBranch.js';
import {
  createInitialSessionState,
  currentNode,
  type BranchSet,
  type SessionNode,
  type SessionReducer,
  type SessionState,
} from './types.js';

/** The window inside which a growing pitch-class set is one arpeggiated chord, not two. */
export const DEFAULT_ARPEGGIO_WINDOW_MS = DEFAULT_RECOGNIZER_PARAMS.arpeggioWindowMs;

export interface SessionReducerOptions {
  /**
   * Detections that strictly extend the current node's set and arrive within this many
   * milliseconds of it replace that node's chord instead of appending a new one.
   * Defaults to the recogniser's own `arpeggioWindowMs`, so the two agree by construction.
   */
  arpeggioWindowMs?: number;
  /**
   * Node id factory. Must be a pure function of its arguments — see the file header.
   * Overridable so a replay harness can re-use the ids from a recorded log verbatim.
   */
  createNodeId?: (state: SessionState, detected: DetectedChord) => string;
}

/**
 * Deterministic node ids: position in the path plus the detection timestamp. Unique
 * within a session (the position always advances), stable across replays, and readable
 * in a debugger, which a UUID is not.
 */
export function defaultNodeId(state: SessionState, detected: DetectedChord): string {
  return `n${state.path.length}-${Math.round(detected.timestamp)}`;
}

/**
 * The set a node is compared against when deciding whether the next detection is "the
 * same chord still sounding".
 *
 * For a node that came from the microphone that is what was actually *heard*, which can
 * be a superset of what the chord symbol implies (an added ninth the recogniser saw but
 * the hypothesis did not name). For a corrected or manually entered node the chord itself
 * is the truth, because there is no longer a detection that describes it.
 */
export function nodePitchClasses(node: SessionNode): PitchClass[] {
  return node.source === 'audio' && node.detected
    ? node.detected.best.pitchClasses
    : node.chord.pitchClasses;
}

/**
 * The spelling a selection brought with it, where it genuinely describes the chord being
 * written — otherwise `null`, meaning "spell it the usual way".
 *
 * `respell` answers a question the recogniser cannot even ask, because a sonority has no
 * letters. A *choice* has already answered it: the player read "D♭m" on the card before
 * they tapped it. Respelling that against a key estimate which includes the chord itself
 * is how "select D♭, get C♯" happened — picking a D♭ minor triad moves the estimate to
 * tonic-pc-1 minor, whose signature is E major's, and pc 1 on the sharp side is C♯.
 *
 * The agreement check is what keeps this honest. A `chosen` chord is only allowed to name
 * the sonority it arrived with, so a caller that hands over a spelling for some other
 * harmony falls back to the mechanical path rather than mislabelling the node. Root is not
 * compared: the key estimate is computed before the substitution happens (see `appendNode`),
 * so a disagreement there could only ever change letters, which is this field's whole remit.
 */
function chosenSpelling(
  chosen: CanonicalChord | undefined,
  hypothesis: ChordHypothesis,
): CanonicalChord | null {
  if (!chosen) return null;
  if (chosen.bassPc !== hypothesis.bassPc) return null;
  return pcSetEquals(chosen.pitchClasses, hypothesis.pitchClasses) ? chosen : null;
}

/** Semitone above the root → the extension that names it. Degree collisions are resolved by the caller. */
const EXTENSION_FOR_SEMITONE: Readonly<Record<number, Extension>> = Object.freeze({
  1: 'b9',
  2: '9',
  3: '#9',
  5: '11',
  6: '#11',
  8: 'b13',
  9: '13',
});

/**
 * Turn a recogniser hypothesis into a chord.
 *
 * The hypothesis names a root, a triad quality and possibly a seventh; the detected set
 * often says more than that. This closes the gap in both directions so that the chord on
 * the node sounds like what was played: degrees the hypothesis implies but that are not
 * sounding become omissions (pianists leave out fifths constantly), and pitch classes
 * that are sounding but unaccounted for become extensions. What cannot be named — a major
 * third over a minor triad, say — is simply left out of the symbol; that is a display
 * concession only, because matching always uses the detected set, never the symbol.
 *
 * Spelling is assigned by `respell` against the key context, per ADR 6.
 */
export function chordFromHypothesis(hypothesis: ChordHypothesis, key: KeyContext): CanonicalChord {
  const { rootPc, bassPc } = hypothesis;
  const sounding = new Set<PitchClass>(hypothesis.pitchClasses);

  const spec: ChordSpec = {
    root: spellPitchClass(rootPc, key),
    quality: hypothesis.quality,
    extensions: [],
    alterations: [],
    omissions: [],
  };
  if (hypothesis.seventh) spec.seventh = hypothesis.seventh;

  if (sounding.size > 0) {
    // A seventh that is sounding but was not claimed. The recogniser's `seventhPenalty`
    // deliberately makes it reluctant to *posit* a seventh, but if the note is in the set
    // then it is being played, and a chord called "C" that sounds a B♭ is a worse answer
    // than one called "C7". Mid-roll this is the common case.
    if (!spec.seventh) {
      if (sounding.has(mod12(rootPc + 11))) spec.seventh = 'maj7';
      else if (sounding.has(mod12(rootPc + 10))) spec.seventh = 'min7';
      else if (spec.quality === 'diminished' && sounding.has(mod12(rootPc + 9)))
        spec.seventh = 'dim7';
    }

    const implied = chordDegreeSemitones(spec);
    const third = implied.get(3);
    if (third !== undefined && !sounding.has(mod12(rootPc + third))) spec.omissions.push('3');
    const fifth = implied.get(5);
    if (fifth !== undefined && !sounding.has(mod12(rootPc + fifth))) spec.omissions.push('5');

    const covered = new Set<PitchClass>([bassPc]);
    const usedDegrees = new Set<number>();
    for (const [degree, semitone] of chordDegreeSemitones(spec)) {
      covered.add(mod12(rootPc + semitone));
      usedDegrees.add(degree);
    }
    for (const pc of hypothesis.pitchClasses) {
      if (covered.has(pc)) continue;
      const named = EXTENSION_FOR_SEMITONE[mod12(pc - rootPc)];
      if (!named) continue;
      // A seventh chord's thirteenth is a plain sixth when there is no seventh under it.
      const extension: Extension = named === '13' && !spec.seventh ? '6' : named;
      if (usedDegrees.has(EXTENSION_DEGREES[extension])) continue;
      spec.extensions.push(extension);
      usedDegrees.add(EXTENSION_DEGREES[extension]);
      covered.add(pc);
    }
    spec.extensions = sortExtensions(spec.extensions);
  }

  if (bassPc !== rootPc) spec.bass = spellPitchClass(bassPc, key);
  return respell(toCanonical(spec), key);
}

/**
 * The node ids whose branch sets survive a move: the one we are on, and the one before it.
 *
 * Keeping the previous set is not housekeeping, it is the retention rule the UI depends
 * on — the branches that were on screen when the chord was played stay available to be
 * drawn faded until the first new suggestion streams in, so the graph never blanks.
 */
export function retainedNodeIds(
  path: readonly SessionNode[],
  currentNodeId: string | null,
): string[] {
  if (!currentNodeId) return [];
  const index = path.findIndex((node) => node.id === currentNodeId);
  if (index < 0) return [currentNodeId];
  const ids = [currentNodeId];
  if (index > 0) ids.push((path[index - 1] as SessionNode).id);
  return ids;
}

function pruneBranchSets(
  branchSets: Record<string, BranchSet>,
  keep: readonly string[],
): Record<string, BranchSet> {
  const next: Record<string, BranchSet> = {};
  for (const id of keep) {
    const set = branchSets[id];
    if (set) next[id] = set;
  }
  return next;
}

/** Every state change goes through here, so `revision` can never be forgotten. */
function bump(state: SessionState, patch: Partial<SessionState>): SessionState {
  return { ...state, ...patch, revision: state.revision + 1 };
}

function replaceNode(
  path: readonly SessionNode[],
  index: number,
  node: SessionNode,
): SessionNode[] {
  const next = path.slice();
  next[index] = node;
  return next;
}

/**
 * The branch set the given path index was chosen *from* — i.e. the one belonging to the
 * node before it. Pruning means this is only present for the last two nodes, which is all
 * the matching ever needs.
 */
function setBeforeIndex(state: SessionState, index: number): BranchSet | undefined {
  if (index <= 0) return undefined;
  const previous = state.path[index - 1];
  return previous ? state.branchSets[previous.id] : undefined;
}

function withBranchSet(
  state: SessionState,
  nodeId: string,
  patch: (existing: BranchSet | undefined) => BranchSet,
): SessionState {
  return bump(state, {
    branchSets: { ...state.branchSets, [nodeId]: patch(state.branchSets[nodeId]) },
  });
}

/**
 * `requestedAt` without a clock.
 *
 * The reducer is not allowed to read the time, and `BRANCHES_LOADING` carries only a node
 * id — so the request is stamped with the moment its node was played, which is both
 * deterministic and the number the UI actually wants ("branches for the chord you played
 * at t"). The gap between that and the wall clock is the request latency, which the store
 * measures where it belongs.
 */
function requestedAtFor(state: SessionState, nodeId: string): number {
  return state.path.find((node) => node.id === nodeId)?.playedAt ?? state.startedAt;
}

export function createSessionReducer(options: SessionReducerOptions = {}): SessionReducer {
  const arpeggioWindowMs = options.arpeggioWindowMs ?? DEFAULT_ARPEGGIO_WINDOW_MS;
  const createNodeId = options.createNodeId ?? defaultNodeId;

  /** 1. The same harmony, still sounding: keep the node, remember the fresher detection. */
  function sustainCurrentNode(
    state: SessionState,
    index: number,
    detected: DetectedChord,
  ): SessionState {
    const node = state.path[index] as SessionNode;
    // `SessionNode` has no duration field; the node's extent is `playedAt` (its onset)
    // through `detected.timestamp` (the last time it was heard), so recording the newer
    // detection *is* extending the duration. It also keeps the chroma current, which is
    // what a later correction is re-scored against.
    return bump(state, { path: replaceNode(state.path, index, { ...node, detected }) });
  }

  /** 1b. Arpeggiation: the same chord becoming clearer. Grow it in place. */
  function growCurrentNode(
    state: SessionState,
    index: number,
    detected: DetectedChord,
    chosen?: CanonicalChord,
  ): SessionState {
    const node = state.path[index] as SessionNode;
    const grown = chordFromHypothesis(detected.best, state.keyEstimate);
    const path = replaceNode(state.path, index, { ...node, chord: grown, detected });

    // Both estimates are taken over the mechanically derived chord, before any chosen
    // spelling is substituted below, so they cannot depend on which letters won.
    const keyAtTime = estimateKey(path.slice(0, index + 1));
    const keyEstimate = estimateKey(path);
    const settled: SessionNode = {
      ...(path[index] as SessionNode),
      chord: chosenSpelling(chosen, detected.best) ?? respell(grown, keyAtTime),
      keyAtTime,
    };
    // The completed chord may match a branch the partial one did not — a rolled Cmaj7
    // arriving as C, then C+E, then C+E+G+B. Re-credit it, or withdraw the credit.
    const matched = findSuggestionByPitchClasses(
      detected.best.pitchClasses,
      setBeforeIndex(state, index),
    );
    if (matched) settled.fromSuggestionId = matched.id;
    else delete settled.fromSuggestionId;

    // The branches offered from this node were generated for the chord it used to be.
    const branchSets = { ...state.branchSets };
    delete branchSets[node.id];

    return bump(state, {
      path: replaceNode(path, index, settled),
      keyEstimate,
      branchSets,
    });
  }

  /** 2 & 3. A new harmony. Matched or not, however it was selected, it is appended. */
  function appendNode(
    state: SessionState,
    detected: DetectedChord,
    source: 'audio' | 'manual',
    chosen?: CanonicalChord,
  ): SessionState {
    const previous = currentNode(state);
    const matched = previous
      ? findSuggestionByPitchClasses(detected.best.pitchClasses, state.branchSets[previous.id])
      : null;

    const provisional = chordFromHypothesis(detected.best, state.keyEstimate);
    const draft: SessionNode = {
      id: createNodeId(state, detected),
      chord: provisional,
      detected,
      source,
      playedAt: detected.timestamp,
      keyAtTime: state.keyEstimate,
    };
    if (matched) draft.fromSuggestionId = matched.id;

    // Spelling follows the key as it stands *including* this chord — a borrowed ♭VI
    // should be spelled by the key it just moved to, not the one it left. Unless the
    // player named the chord themselves, in which case they have already spelled it and
    // the estimate does not get to argue; `draft` still carries the mechanical chord, so
    // the estimate below is computed over exactly what it always was.
    const path = [...state.path, draft];
    const keyEstimate = estimateKey(path);
    const node: SessionNode = {
      ...draft,
      chord: chosenSpelling(chosen, detected.best) ?? respell(provisional, keyEstimate),
      keyAtTime: keyEstimate,
    };
    path[path.length - 1] = node;

    return bump(state, {
      path,
      currentNodeId: node.id,
      keyEstimate,
      branchSets: pruneBranchSets(state.branchSets, retainedNodeIds(path, node.id)),
      listening:
        state.listening === 'awaiting-first-chord' || state.listening === 'idle'
          ? 'active'
          : state.listening,
    });
  }

  function handleChordDetected(
    state: SessionState,
    detected: DetectedChord,
    source: 'audio' | 'manual',
    chosen?: CanonicalChord,
  ): SessionState {
    // Nothing is sounding. Not a chord, not an error, not a node.
    if (detected.best.pitchClasses.length === 0) return state;
    // While paused the microphone belongs to voice input; a stray detection is not a chord
    // the player meant to contribute.
    if (state.listening === 'paused') return state;

    const index = state.currentNodeId
      ? state.path.findIndex((node) => node.id === state.currentNodeId)
      : -1;

    if (index >= 0) {
      const node = state.path[index] as SessionNode;
      const heard = nodePitchClasses(node);
      if (pcSetEquals(detected.best.pitchClasses, heard)) {
        return sustainCurrentNode(state, index, detected);
      }
      if (
        pcSetIsStrictSuperset(detected.best.pitchClasses, heard) &&
        detected.timestamp - node.playedAt <= arpeggioWindowMs
      ) {
        return growCurrentNode(state, index, detected, chosen);
      }
    }

    return appendNode(state, detected, source, chosen);
  }

  const reduce: SessionReducer = (state, action) => {
    switch (action.type) {
      case 'SESSION_STARTED': {
        const fresh = createInitialSessionState({
          sessionId: action.sessionId,
          // `createInitialSessionState` would otherwise reach for `Date.now()`, and the
          // reducer may not. The store creates the state it dispatches this into.
          startedAt: state.startedAt,
          perspectives: action.perspectives,
          adventurousness: state.adventurousness,
        });
        if (action.intention !== undefined) fresh.intention = action.intention;
        const usable = state.micStatus !== 'denied' && state.micStatus !== 'unavailable';
        return {
          ...fresh,
          revision: state.revision + 1,
          micStatus: state.micStatus,
          listening: usable ? 'awaiting-first-chord' : 'idle',
        };
      }

      case 'MIC_STATUS': {
        if (state.micStatus === action.status) return state;
        let listening = state.listening;
        if (action.status === 'granted' && listening === 'idle') listening = 'awaiting-first-chord';
        if (action.status === 'denied' || action.status === 'unavailable') listening = 'idle';
        return bump(state, { micStatus: action.status, listening });
      }

      case 'CHORD_DETECTED':
        // Absent `source` means the microphone, which is what every caller meant before
        // discrete input existed. Nothing else about the transition changes. Absent
        // `chosen` — every audio dispatch, and every action log recorded before selections
        // carried their spelling — means the mechanical spelling, likewise.
        return handleChordDetected(state, action.detected, action.source ?? 'audio', action.chosen);

      case 'BRANCHES_LOADING':
        return withBranchSet(state, action.nodeId, (existing) => ({
          forNodeId: action.nodeId,
          // A refetch keeps whatever already arrived on screen rather than blanking it.
          suggestions: existing?.suggestions ?? [],
          status: 'loading',
          requestedAt: requestedAtFor(state, action.nodeId),
        }));

      case 'BRANCH_RECEIVED': {
        const existing = state.branchSets[action.nodeId];
        const suggestions = existing ? existing.suggestions.slice() : [];
        const at = suggestions.findIndex((s) => s.id === action.suggestion.id);
        if (at >= 0) suggestions[at] = action.suggestion;
        else suggestions.push(action.suggestion);
        return withBranchSet(state, action.nodeId, () => ({
          forNodeId: action.nodeId,
          suggestions,
          status: 'partial',
          requestedAt: existing?.requestedAt ?? requestedAtFor(state, action.nodeId),
        }));
      }

      case 'BRANCHES_READY': {
        const existing = state.branchSets[action.nodeId];
        if (existing?.status === 'ready' && state.llmHealthy) return state;
        return {
          ...withBranchSet(state, action.nodeId, () => ({
            forNodeId: action.nodeId,
            suggestions: existing?.suggestions ?? [],
            status: 'ready',
            requestedAt: existing?.requestedAt ?? requestedAtFor(state, action.nodeId),
          })),
          // A completed set is the only proof that the model is answering again.
          llmHealthy: true,
        };
      }

      case 'BRANCHES_FAILED': {
        const existing = state.branchSets[action.nodeId];
        const fallback = action.fallback ?? [];
        const usable = fallback.length > 0;
        return {
          ...withBranchSet(state, action.nodeId, () => ({
            forNodeId: action.nodeId,
            // Locally generated continuations if we have them; otherwise whatever had
            // already streamed in stays on screen. Failure never empties the graph.
            suggestions: usable ? fallback : (existing?.suggestions ?? []),
            status: usable ? 'fallback' : 'failed',
            requestedAt: existing?.requestedAt ?? requestedAtFor(state, action.nodeId),
          })),
          // Even a good fallback means the model did not answer, and the UI should say so.
          llmHealthy: false,
        };
      }

      case 'CORRECTION_APPLIED': {
        const index = state.path.findIndex((node) => node.id === action.nodeId);
        if (index < 0) return state;
        const node = state.path[index] as SessionNode;

        const keyAtTime = estimateKey([
          ...state.path.slice(0, index),
          { ...node, chord: action.chord },
        ]);
        // Verbatim, not respelled. A correction only ever arrives from the sheet, where
        // the chord was displayed to the player and confirmed by them — so this is the
        // same explicit spelling `chosen` protects on the way in, and overriding it here
        // would be the app arguing with the fix it was just handed.
        const corrected: SessionNode = {
          ...node,
          chord: action.chord,
          source: 'correction',
          keyAtTime,
        };
        // The old credit belonged to a chord that was never played. Re-earn it or lose it.
        const matched = findSuggestionByPitchClasses(
          action.chord.pitchClasses,
          setBeforeIndex(state, index),
        );
        if (matched) corrected.fromSuggestionId = matched.id;
        else delete corrected.fromSuggestionId;

        const path = replaceNode(state.path, index, corrected);

        // Every branch set from the corrected node onwards was generated from a chord that
        // was never played. Discard them; the store notices the current node has no set and
        // refetches.
        const branchSets = { ...state.branchSets };
        for (let i = index; i < path.length; i += 1) delete branchSets[(path[i] as SessionNode).id];

        return bump(state, { path, branchSets, keyEstimate: estimateKey(path) });
      }

      case 'STEP_BACK': {
        if (action.count <= 0 || state.path.length === 0) return state;
        const path = state.path.slice(0, Math.max(0, state.path.length - action.count));
        const currentNodeId = path.length > 0 ? (path[path.length - 1] as SessionNode).id : null;
        return bump(state, {
          path,
          currentNodeId,
          keyEstimate: estimateKey(path),
          branchSets: pruneBranchSets(state.branchSets, retainedNodeIds(path, currentNodeId)),
          listening:
            path.length === 0 && state.listening === 'active'
              ? 'awaiting-first-chord'
              : state.listening,
        });
      }

      case 'SET_INTENTION':
        if (state.intention === action.intention) return state;
        return bump(state, { intention: action.intention });

      case 'SET_PERSPECTIVES':
        return bump(state, { perspectives: [...action.perspectives] });

      case 'SET_ADVENTUROUSNESS': {
        const value = Math.min(1, Math.max(0, action.value));
        if (state.adventurousness === value) return state;
        return bump(state, { adventurousness: value });
      }

      case 'LLM_HEALTH':
        if (state.llmHealthy === action.healthy) return state;
        return bump(state, { llmHealthy: action.healthy });

      case 'RESTORED':
        // Wholesale replacement, as documented on the action — the caller validates and
        // normalises before dispatching (see `state/persistence.ts`). `revision` is kept
        // as it was persisted, so rehydrating does not immediately trigger a re-save.
        return {
          ...action.state,
          perspectives: [...action.state.perspectives],
          path: action.state.path.slice(),
          branchSets: { ...action.state.branchSets },
          keyEstimate: { ...action.state.keyEstimate },
        };

      default: {
        const exhaustive: never = action;
        void exhaustive;
        return state;
      }
    }
  };

  return reduce;
}

/** The reducer the application uses. See `SessionReducer` in `types.ts` for the contract. */
export const sessionReducer: SessionReducer = createSessionReducer();
